import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Fade,
} from '@mui/material';
import Navbar_public from './Navbar_public';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';

const Donation = () => {
  const [formData, setFormData] = useState({ amount: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [yocoSDK, setYocoSDK] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.yoco.com/sdk/v1/yoco-sdk-web.js';
    script.async = true;
    script.onload = () => {
      setYocoSDK(new window.YocoSDK({
        publicKey: 'pk_test_5ace71238oP1KoG9edd4',
      }));
    };
    script.onerror = () => setError('Failed to load payment processor');
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const initiateYocoPayment = async () => {
    setLoading(true);
    setError(null);

    if (!yocoSDK) {
      setError('Payment processor not ready yet');
      return setLoading(false);
    }

    try {
      const amountInCents = Math.round(parseFloat(formData.amount) * 100);
      yocoSDK.showPopup({
        amountInCents,
        currency: 'ZAR',
        name: '4EVR Donation',
        description: 'Memory preservation donation',
        callback: (result) => {
          if (result.error) {
            setError(result.error.message);
            setSuccess(false);
          } else {
            setSuccess(true);
            setFormData({ amount: '' });
          }
          setLoading(false);
        }
      });
    } catch (error) {
      setError(error.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.amount || isNaN(formData.amount)) {
      return setError('Please enter a valid amount');
    }
    if (parseFloat(formData.amount) <= 0) {
      return setError('Amount must be greater than 0');
    }
    initiateYocoPayment();
  };

  const handleBack = () => navigate(-1);

  return (
    <div>
      <Navbar_public />
      <Box
        sx={{
          backgroundImage: 'url(images/background6.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          minWidth: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#fff',
        }}
      >
        <Fade in timeout={800}>
          <Paper elevation={10} sx={{
            width: '70%',
            maxWidth: '800px',
            p: 4,
            borderRadius: 4,
            backgroundColor: 'rgba(238, 238, 238, 0.9)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <Box textAlign="center" mb={4}>
              <FavoriteIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h3" gutterBottom sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #6a1b9a, #3f51b5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Love Offering
              </Typography>
              <Typography variant="body1" sx={{
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.6,
              }}>
                Behind 4EVR.ORG.ZA is a team dedicated to preserving memories.
                Your gift helps keep your loved one's legacy alive.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                Thank you for your generous donation! Your support means the world to us.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                variant="outlined"
                label="Donation Amount (ZAR)"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, fontWeight: 'bold' }}>R</Typography>,
                  sx: {
                    fontSize: '1.2rem',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }
                }}
                InputLabelProps={{
                  sx: { color: 'text.secondary' }
                }}
                sx={{ mb: 3 }}
              />

              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={handleBack}
                    disabled={loading}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      borderWidth: 2,
                      '&:hover': { borderWidth: 2 },
                    }}
                  >
                    Go Back
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || !yocoSDK}
                    endIcon={!loading && <FavoriteIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, #6a1b9a, #3f51b5)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #7b1fa2, #303f9f)',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Donate Now'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>

            <Box mt={4} textAlign="center" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                component="img"
                src="/images/yoco.png"
                alt="Yoco"
                sx={{
                  height: 40,
                  mb: 1,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Secure payments powered by Yoco
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </div>
  );
};

export default Donation;